const LEGACY_POLICY_FILE_KEY = 'policy-file';
const LEGACY_POLICY_VIEWPORT_KEY = 'policy-viewport';
const LEGACY_PROJECT_KEY = 'project';

const isObject = (value) => value !== null && typeof value === 'object' && !Array.isArray(value);

const getStorage = () => {
    try {
        if (typeof window === 'undefined' || !window.localStorage) {
            return null;
        }
        return window.localStorage;
    } catch (error) {
        return null;
    }
};

export const safeParseJSON = (rawValue, fallback = null) => {
    if (rawValue === undefined || rawValue === null || rawValue === '') {
        return fallback;
    }

    try {
        return JSON.parse(rawValue);
    } catch (error) {
        return fallback;
    }
};

export const getLocalStorageJSON = (key, fallback = null) => {
    const storage = getStorage();
    if (!storage) {
        return fallback;
    }

    const raw = readRawValue(storage, key);
    if (raw === null) {
        return fallback;
    }

    const parsed = safeParseJSON(raw, undefined);
    if (parsed === undefined) {
        removeStorageKey(storage, key);
        return fallback;
    }

    return parsed;
};

export const setLocalStorageJSON = (key, value) => {
    const storage = getStorage();
    if (!storage) {
        return { ok: false, reason: 'storage-unavailable' };
    }

    const serialized = JSON.stringify(value);

    try {
        storage.setItem(key, serialized);
        return { ok: true };
    } catch (error) {
        return { ok: false, reason: 'write-failed', error };
    }
};

const readRawValue = (storage, key) => {
    try {
        return storage.getItem(key);
    } catch (error) {
        return null;
    }
};

const removeStorageKey = (storage, key) => {
    try {
        storage.removeItem(key);
    } catch (error) {
        // no-op
    }
};

const readLegacyJSON = (storage, key) => {
    const raw = readRawValue(storage, key);
    if (raw === null) {
        return null;
    }

    const parsed = safeParseJSON(raw, undefined);
    if (parsed === undefined) {
        removeStorageKey(storage, key);
        return null;
    }

    return parsed;
};

export const getProjectFromStorage = () => {
    const storage = getStorage();
    if (!storage) {
        return null;
    }

    const project = readLegacyJSON(storage, LEGACY_PROJECT_KEY);
    return isObject(project) ? project : null;
};

export const getPolicyFileFromStorage = ({ normalize } = {}) => {
    const storage = getStorage();
    if (!storage) {
        return null;
    }

    const legacyPayload = readLegacyJSON(storage, LEGACY_POLICY_FILE_KEY);
    if (legacyPayload === null) {
        return null;
    }

    return typeof normalize === 'function' ? normalize(legacyPayload) : legacyPayload;
};

export const setPolicyFileToStorage = (policyFile, { syncLegacy = true } = {}) => {
    if (!syncLegacy) {
        return { ok: true };
    }

    return setLocalStorageJSON(LEGACY_POLICY_FILE_KEY, policyFile);
};

export const getPolicyViewportFromStorage = ({ policyId } = {}) => {
    const storage = getStorage();
    if (!storage) {
        return null;
    }

    const legacyStore = readLegacyJSON(storage, LEGACY_POLICY_VIEWPORT_KEY);
    if (!isObject(legacyStore)) {
        return null;
    }

    const legacyPayload = legacyStore?.[policyId];
    if (!isObject(legacyPayload)) {
        return null;
    }

    return legacyPayload;
};

export const setPolicyViewportToStorage = (viewportPayload, { policyId, syncLegacy = true } = {}) => {
    if (!policyId) {
        return { ok: false, reason: 'missing-policy-id' };
    }

    if (!syncLegacy) {
        return { ok: true };
    }

    const storage = getStorage();
    if (!storage) {
        return { ok: false, reason: 'storage-unavailable' };
    }

    const legacyStore = readLegacyJSON(storage, LEGACY_POLICY_VIEWPORT_KEY) || {};
    const nextLegacyStore = {
        ...legacyStore,
        [policyId]: viewportPayload,
    };

    return setLocalStorageJSON(LEGACY_POLICY_VIEWPORT_KEY, nextLegacyStore);
};

export const removePolicyStorageForPolicy = ({ policyId } = {}) => {
    const storage = getStorage();
    if (!storage) {
        return 0;
    }

    if (!policyId) {
        return 0;
    }

    let removedCount = 0;

    const legacyViewportStore = readLegacyJSON(storage, LEGACY_POLICY_VIEWPORT_KEY);
    if (isObject(legacyViewportStore) && Object.prototype.hasOwnProperty.call(legacyViewportStore, policyId)) {
        const nextViewportStore = { ...legacyViewportStore };
        delete nextViewportStore[policyId];
        setLocalStorageJSON(LEGACY_POLICY_VIEWPORT_KEY, nextViewportStore);
        removedCount += 1;
    }

    const legacyPolicyFile = readLegacyJSON(storage, LEGACY_POLICY_FILE_KEY);
    if (legacyPolicyFile?.policy_diagram_id === policyId) {
        removeStorageKey(storage, LEGACY_POLICY_FILE_KEY);
        removedCount += 1;
    }

    return removedCount;
};
